import {Injectable} from "@angular/core";
import {SFTPAPIService} from "@app/services/sftp-api/sftp-api.service";
import {BehaviorSubject, catchError, finalize, Observable, Subject, takeUntil, tap, throwError} from "rxjs";
import {UserAuth} from "@app/services/sftp-api/models";

@Injectable()
export class AuthorizationService {
  private _username$: BehaviorSubject<string>;
  private _password$: BehaviorSubject<string>;
  private _loginError: string;
  private _loginInProgress = false;
  private _userAuthorized = false;

  private _destroy$: Subject<void>;

  constructor(private sftpAPIService: SFTPAPIService) {}

  get loginError(): string {
    return this._loginError;
  }

  get loginInProgress(): boolean {
    return this._loginInProgress;
  }

  get userAuthorized(): boolean {
    return this._userAuthorized;
  }

  set userAuthorized(value: boolean) {
    this._userAuthorized = value;
  }

  getUsername(): string {
    return this._username$.value;
  }

  username$(): Observable<string> {
    return this._username$.asObservable();
  }

  password$(): Observable<string> {
    return this._password$.asObservable();
  }

  initialize(): void {
    this._username$ = new BehaviorSubject<string>(null);
    this._password$ = new BehaviorSubject<string>(null);

    this._loginError = null;
    this._loginInProgress = false;
    this._userAuthorized = false;

    this._destroy$ = new Subject<void>();
  }

  destroy(): void {
    this._username$.complete();
    this._username$ = null;

    this._password$.complete();
    this._password$ = null;

    this._loginError = null;
    this._loginInProgress = false;
    this._userAuthorized = false;

    this._destroy$.next();
    this._destroy$.complete();
    this._destroy$ = null;
  }

  userAuthorization(userAuth: UserAuth): Observable<void> {
    this._username$.next(userAuth.username);
    this._password$.next(userAuth.password);

    this._loginInProgress = true;

    return this.sftpAPIService.authenticate(userAuth).pipe(
      takeUntil(this._destroy$),
      tap(() => {
        this._loginError = null;
        this._userAuthorized = true;
      }),
      catchError((error) => {
        this._loginError = error.error.message;
        this._userAuthorized = false;

        return throwError(error);
      }),
      finalize(() => {
        this._loginInProgress = false;
      })
    );
  }

  userLogout(): Observable<void> {
    return this.sftpAPIService.logout().pipe(
      tap(() => {
        this._userAuthorized = false;
      })
    );
  }
}
